import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type ContainerKind = 'sandbox' | 'project';
/** Route has no workspace container truth until snapshot (e.g. `/assessments/:id`); do not show sandbox/project from LS as authority. */
export type WorkspaceContainerResolution = 'live' | 'assessment_pending';
export type RecentProject = { id: string; label: string };

const LS = { k: 'orrekie-workspace-kind', id: 'orrekie-workspace-project-id', lb: 'orrekie-workspace-project-label', rc: 'orrekie-workspace-project-recents' };

function isAssessmentDeepLinkPath(pathname: string): boolean {
  return /^\/assessments\/[^/]+\/?$/.test(pathname);
}

const Z = {
  g: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  s: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      void 0;
    }
  },
  d: (k: string) => {
    try {
      localStorage.removeItem(k);
    } catch {
      void 0;
    }
  },
};

function readRecents(): RecentProject[] {
  try {
    const raw = Z.g(LS.rc);
    if (!raw) return [];
    const p = JSON.parse(raw) as unknown;
    if (!Array.isArray(p)) return [];
    return p
      .filter((x): x is { id: string; label?: string } => Boolean(x) && typeof x === 'object' && typeof (x as { id?: string }).id === 'string')
      .map((x) => ({ id: x.id, label: typeof x.label === 'string' ? x.label : x.id }))
      .slice(0, 12);
  } catch {
    return [];
  }
}
function writeRecents(r: RecentProject[]) {
  try {
    localStorage.setItem(LS.rc, JSON.stringify(r.slice(0, 12)));
  } catch {
    void 0;
  }
}

type W = {
  containerKind: ContainerKind;
  projectId: string | null;
  projectLabel: string;
  workspaceContainerResolution: WorkspaceContainerResolution;
  recentProjects: RecentProject[];
  setSandbox: () => void;
  setProject: (id: string, label?: string) => void;
  rememberRecentProject: (id: string, label: string) => void;
};
type O = {
  objectLine: string | null;
  setObjectLine: (v: string | null) => void;
  /**
   * U-value lane only: href to the design-option detail route when the shell object line refers to a
   * design option. Not a generalized object-routing primitive — see workspace / object ADRs before
   * reusing this shape elsewhere.
   */
  designOptionDestinationHref: string | null;
  setDesignOptionDestinationHref: (v: string | null) => void;
};

const WC = createContext<W | null>(null);
const OC = createContext<O | null>(null);

export function WorkspaceProviders({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [workspaceContainerResolution, setWorkspaceContainerResolution] = useState<WorkspaceContainerResolution>(() =>
    isAssessmentDeepLinkPath(location.pathname) ? 'assessment_pending' : 'live',
  );
  const [containerKind, setContainerKind] = useState<ContainerKind>('sandbox');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectLabel, setProjectLabel] = useState('Project');
  const [recentProjects, setRecentProjects] = useState<RecentProject[]>(() => (typeof window !== 'undefined' ? readRecents() : []));
  const [objectLine, setObjectLineState] = useState<string | null>(null);
  const [designOptionDestinationHref, setDesignOptionDestinationHrefState] = useState<string | null>(null);

  useEffect(() => {
    if (isAssessmentDeepLinkPath(location.pathname)) {
      return;
    }
    const k = Z.g(LS.k) as ContainerKind | null;
    const pid = Z.g(LS.id)?.trim();
    const pl = Z.g(LS.lb)?.trim();
    if (k === 'project' && pid) {
      setContainerKind('project');
      setProjectId(pid);
      if (pl) setProjectLabel(pl);
    } else {
      setContainerKind('sandbox');
      setProjectId(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    const { pathname, search } = location;
    const q = new URLSearchParams(search);
    if (isAssessmentDeepLinkPath(pathname)) {
      setWorkspaceContainerResolution('assessment_pending');
      setContainerKind('sandbox');
      setProjectId(null);
      setProjectLabel('Project');
      return;
    }
    setWorkspaceContainerResolution('live');
    if (pathname === '/sandbox' || pathname === '/workspace/sandbox') {
      setContainerKind('sandbox');
      setProjectId(null);
      Z.s(LS.k, 'sandbox');
      Z.d(LS.id);
      return;
    }
    const m = /^\/projects\/([^/]+)\/?$/.exec(pathname);
    const legacyM = /^\/workspace\/project\/([^/]+)\/?$/.exec(pathname);
    const projectRouteId = m?.[1] ?? legacyM?.[1];
    if (projectRouteId) {
      const id = projectRouteId;
      if (id === 'new') return;
      setContainerKind('project');
      setProjectId(id);
      const fr = readRecents().find((r) => r.id === id);
      if (fr?.label) setProjectLabel(fr.label);
      Z.s(LS.k, 'project');
      Z.s(LS.id, id);
      return;
    }
    if (pathname === '/u-value/calculator') {
      const pid = q.get('projectId')?.trim();
      if (pid) {
        setContainerKind('project');
        setProjectId(pid);
        Z.s(LS.k, 'project');
        Z.s(LS.id, pid);
      }
      return;
    }
    const doM = /^\/u-value\/design-options\/([^/]+)\/([^/]+)\/?$/.exec(pathname);
    if (doM) {
      const pid = decodeURIComponent(doM[1]);
      if (pid) {
        setContainerKind('project');
        setProjectId(pid);
        Z.s(LS.k, 'project');
        Z.s(LS.id, pid);
      }
    }
  }, [location]);

  const setSandbox = useCallback(() => {
    setWorkspaceContainerResolution('live');
    setContainerKind('sandbox');
    setProjectId(null);
    Z.s(LS.k, 'sandbox');
    Z.d(LS.id);
  }, []);

  const rememberRecentProject = useCallback((id: string, label: string) => {
    const t = id.trim();
    if (!t) return;
    setRecentProjects((prev) => {
      const next = [{ id: t, label: label.trim() || t }, ...prev.filter((p) => p.id !== t)];
      writeRecents(next);
      return next.slice(0, 12);
    });
  }, []);

  const setProject = useCallback(
    (id: string, label?: string) => {
      const t = id.trim();
      if (!t) {
        setSandbox();
        return;
      }
      setWorkspaceContainerResolution('live');
      setContainerKind('project');
      setProjectId(t);
      const lab = (label ?? t).trim();
      setProjectLabel(lab);
      rememberRecentProject(t, lab);
      Z.s(LS.k, 'project');
      Z.s(LS.id, t);
      Z.s(LS.lb, lab);
    },
    [setSandbox, rememberRecentProject],
  );

  const workspaceValue = useMemo(
    () => ({
      containerKind,
      projectId,
      projectLabel,
      workspaceContainerResolution,
      recentProjects,
      setSandbox,
      setProject,
      rememberRecentProject,
    }),
    [containerKind, projectId, projectLabel, workspaceContainerResolution, recentProjects, setSandbox, setProject, rememberRecentProject],
  );
  const objectValue = useMemo(
    () => ({
      objectLine,
      setObjectLine: setObjectLineState,
      designOptionDestinationHref,
      setDesignOptionDestinationHref: setDesignOptionDestinationHrefState,
    }),
    [objectLine, designOptionDestinationHref],
  );

  return (
    <WC.Provider value={workspaceValue}>
      <OC.Provider value={objectValue}>{children}</OC.Provider>
    </WC.Provider>
  );
}

export function useWorkspace() {
  const c = useContext(WC);
  if (!c) throw new Error('useWorkspace requires WorkspaceProviders');
  return c;
}
export function useWorkspaceObject() {
  const c = useContext(OC);
  if (!c) throw new Error('useWorkspaceObject requires WorkspaceProviders');
  return c;
}
